/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { useEffect } from "react";

import EditorIcon from "@/assets/img/symbolic/editor-hat.svg?react";

interface ToggleSwitchProps
{
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;

    showToolTip?: boolean;
    setShowToolTip?: (show: boolean) => void;
}

const darkBlue = "#011936";
const poppyYellow = "#FECB6B";

const Label = Wind.style({
    display: "inline-block",
    position: "relative",
    width: "w-12",
    height: "h-7",
    cursor: "cursor-pointer",
    transitionProperty: "transition-opacity",
    transitionDuration: "duration-300",
    transitionTimingFunction: "ease-in-out",
    userSelect: "select-none",
}).class;

const HiddenInput = Wind.style({
    display: "hidden",
}).class;

const Track = Wind.style({
    position: "absolute",
    top: "top-0",
    left: "left-0",
    right: "right-0",
    bottom: "bottom-0",
    borderRadius: "rounded-full",
    transitionProperty: "transition-colors",
    transitionDuration: "duration-300",
    boxShadow: "shadow",
}).class;

const Thumb = Wind.style({
    position: "absolute",
    top: "top-[0.75rem]",
    left: "left-[0.75rem]",
    width: "w-[22px]",
    height: "h-[22px]",
    borderRadius: "rounded-full",
    transitionProperty: "transition-all",
    transitionDuration: "duration-300",
    borderWidth: "border-2",
    boxShadow: "shadow",
}).class;

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
    checked,
    onChange,
    disabled = false,
    showToolTip,
    setShowToolTip,
}) =>
{
    const set = React.useCallback((value: boolean) =>
    {
        if (disabled) return;
        onChange(value);
    }, [disabled, onChange]);

    React.useEffect(() =>
    {
        if (disabled) setShowToolTip?.(false);
    }, [disabled, setShowToolTip]);

    useEffect(() =>
    {
        const handleClickOutside = (event: MouseEvent) =>
        {
            if (!event.target || !(event.target instanceof Element)) return;
            if (!event.target.closest(".editorIcon")) setShowToolTip?.(false);
        };

        document.addEventListener("click", handleClickOutside);

        return () => void document.removeEventListener("click", handleClickOutside);
    }, [setShowToolTip, disabled, checked, onChange, set]);

    return (
        <div className="relative inline-flex gap-3 items-center">
            <EditorIcon

                className={`w-7 h-7 ${checked ? "text-poppy-yellow" : "text-white"} editorIcon`}
                onClick={() =>
                {
                    if (disabled) return;
                    setShowToolTip?.(!showToolTip);
                }}
            />
            <label
                className={Label}
                style={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.6 : 1,
                }}
            >

                <input
                    className={HiddenInput}
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => void set(e.target.checked)}
                />
                <div
                    className={Track}
                    style={{
                        background: checked ? darkBlue : "#222",
                        boxShadow: checked
                            ? `0 0 0 2px ${poppyYellow}55`
                            : "0 1px 4px #0003",
                    }}
                />
                <div
                    className={Thumb}
                    style={{
                        left: checked ? 24 : 3,
                        background: checked ? poppyYellow : "#fff",
                        boxShadow: checked
                            ? "0 2px 8px #ffd60055"
                            : "0 1px 4px #0002",
                        border: checked ? `2px solid ${poppyYellow}` : "2px solid #eee",
                        top: 3,
                    }}
                />
            </label>
        </div>
    );
};

export default ToggleSwitch;
